import { Graphics, type Sprite } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import { FAMILIAR, FIRE } from '../../sim/full/attacks/cycle';
import type { PropState, SimEvent, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { mixColor } from '../palette';
import { add, byId, hide, setTint } from '../util';
import { ViewPool } from '../views';
import { flagColor, flagWhip } from './avatar-flags';
import { ELEMENT_COLORS } from './avatar-paint';

const WHITE = 0xffffff;
const OUT = 0x040612;
const CORE = { radius: 6, heart: 3.5, white: 0.85 } as const;
const GLOW = { radius: 19, whip: 9, alpha: 0.65, whipAlpha: 0.3 } as const;
// The streak is the way it came over its last tick, fading to nothing at its tail.
const STREAK = { ticks: 1.1, least: 3, steps: 6, width: 4.6, core: 1.9, taper: 0.75 } as const;
const DAY = { width: 1.2, alpha: 0.5 } as const;
const IMPACT = { beats: 0.6, glow: 12, shrink: 0.6, ring: 3, spread: 15, flashTicks: 1 } as const;

export function createAvatarFamiliar(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const avatars = new Set<string>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'cycle') {
      avatars.add(classId);
    }
  }
  const burster = createBurster(ctx, 64);
  const bodies = new Graphics();
  layers.weapons.addChild(bodies);
  const glows = new ViewPool<Sprite>(
    () => add(layers.glow, textures.halo),
    (glow) => {
      hide(glow);
    },
  );

  function streak(fromX: number, fromY: number, toX: number, toY: number, color: number): void {
    const length = Math.hypot(toX - fromX, toY - fromY);
    const acrossX = -(toY - fromY) / length;
    const acrossY = (toX - fromX) / length;
    for (const [width, tint, reach] of [
      [STREAK.width, color, 1],
      [STREAK.core, mixColor(color, WHITE, 0.7), 0.6],
    ] as const) {
      for (let step = 0; step < STREAK.steps; step += 1) {
        const near = (step / STREAK.steps) * reach;
        const far = ((step + 1) / STREAK.steps) * reach;
        const nearWidth = width * (1 - STREAK.taper * near);
        const farWidth = width * (1 - STREAK.taper * far);
        const nx = lerp(fromX, toX, near);
        const ny = lerp(fromY, toY, near);
        const fx = lerp(fromX, toX, far);
        const fy = lerp(fromY, toY, far);
        bodies
          .poly([
            nx + acrossX * nearWidth,
            ny + acrossY * nearWidth,
            fx + acrossX * farWidth,
            fy + acrossY * farWidth,
            fx - acrossX * farWidth,
            fy - acrossY * farWidth,
            nx - acrossX * nearWidth,
            ny - acrossY * nearWidth,
          ])
          .fill({ color: tint, alpha: 0.95 * (1 - (near + far) / (2 * reach)) });
      }
    }
  }

  function draw(prop: PropState, alpha: number, frame: Frame): void {
    const { calm, light, tempo } = frame;
    const beats = (frame.now - tempo.originTick) / tempo.ticksPerBeat;
    const color = flagColor(beats, calm, tempo);
    const whip = flagWhip(beats, calm);
    const x = lerp(prop.prevX, prop.x, alpha);
    const y = lerp(prop.prevY, prop.y, alpha);
    const tailX = x - (prop.x - prop.prevX) * STREAK.ticks;
    const tailY = y - (prop.y - prop.prevY) * STREAK.ticks;
    if (Math.hypot(tailX - x, tailY - y) > STREAK.least) {
      streak(x, y, tailX, tailY, color);
    }
    const glow = glows.acquire(prop.id);
    glow.visible = true;
    glow.position.set(x, y);
    glow.scale.set((GLOW.radius + GLOW.whip * whip) / textures.halo.radius);
    glow.alpha = (GLOW.alpha + GLOW.whipAlpha * whip) * light.haloAlpha;
    setTint(glow, color);
    if (!light.additive) {
      bodies
        .circle(x, y, CORE.radius)
        .stroke({ width: DAY.width * 2, color: OUT, alpha: DAY.alpha });
    }
    bodies
      .circle(x, y, CORE.radius)
      .fill(color)
      .circle(x, y, CORE.heart)
      .fill(mixColor(color, WHITE, CORE.white));
  }

  function struck(state: SimState, event: Extract<SimEvent, { type: 'enemyHit' }>, frame: Frame) {
    const familiar = state.props?.find(
      (prop) =>
        prop.kind === FAMILIAR &&
        prop.playerId === event.by &&
        prop.prevX === event.fromX &&
        prop.prevY === event.fromY,
    );
    if (familiar === undefined) {
      return;
    }
    const { now, tempo } = frame;
    const radius = byId(state.enemies, event.id)?.radius ?? 10;
    const tint = ELEMENT_COLORS[familiar.rank ?? FIRE] ?? ELEMENT_COLORS[FIRE];
    const duration = IMPACT.beats * tempo.ticksPerBeat;
    const at = { now, x: event.x, y: event.y, duration } as const;
    burster.spawn(frame, {
      ...at,
      shape: textures.halo,
      fromRadius: radius + IMPACT.glow,
      toRadius: (radius + IMPACT.glow) * IMPACT.shrink,
      tint,
      peak: 0.6 * frame.light.haloAlpha,
    });
    burster.spawn(frame, {
      ...at,
      shape: textures.ring,
      fromRadius: radius + IMPACT.ring,
      toRadius: radius + IMPACT.ring + IMPACT.spread,
      tint,
      peak: 0.95,
      outline: true,
    });
    if (!frame.calm) {
      burster.spawn(frame, {
        ...at,
        duration: IMPACT.flashTicks,
        shape: textures.halo,
        fromRadius: radius * 1.6,
        toRadius: radius * 1.4,
        tint: WHITE,
        peak: 0.7,
      });
    }
  }

  return {
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (avatars.size > 0 && event.type === 'enemyHit' && event.fromX !== undefined) {
        struck(state, event, frame);
      }
    },
    reset(): void {
      bodies.clear();
      burster.bursts.clear();
      glows.releaseAll();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (avatars.size === 0) {
        return;
      }
      bodies.clear();
      glows.begin();
      for (const prop of state.props ?? []) {
        if (prop.kind === FAMILIAR) {
          draw(prop, alpha, frame);
        }
      }
      glows.end();
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      bodies.destroy();
    },
  };
}
